import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface NutritionAnalytics {
  total_diets: number;
  pets_without_provider: number;
  prescription_diets: number;
  top_brand: {
    brand?: string;
    current_count?: number;
    growth_percentage?: number;
  };
  market_share: Array<{
    brand: string;
    pet_count: number;
    market_share_percentage: number;
  }>;
  brand_switching: Array<{
    from_brand: string;
    to_brand: string;
    switch_count: number;
  }>;
}

export interface PetForAssignment {
  pet_id: string;
  pet_name: string;
  species: string;
  breed: string;
  owner_name: string;
  city: string;
  state: string;
  country: string;
  brand: string;
  food_type: string;
  diet_updated_at: string;
  provider_id: string | null;
  provider_name: string | null;
}

export interface ActiveProvider {
  id: string;
  business_name: string;
  provider_type: string;
  city: string;
  verified: boolean;
}

export interface BrandAlias {
  id: string;
  alias_text: string;
  occurrence_count: number;
  status: string;
  canonical_brand_id: number | null;
  canonical_brand_name: string | null;
  is_farm_animal: boolean;
  canonical_feed_category_id: number | null;
  canonical_feed_category_name: string | null;
}

export interface FarmFeedCategory {
  id: number;
  name: string;
  description: string;
  applies_to_species: string[];
}

export interface GeoDistribution {
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
  premium_count: number;
  economy_count: number;
  prescription_count: number;
  total_count: number;
}

// Hook: Get Available Countries for Nutrition Analytics
export const useNutritionCountries = () => {
  return useQuery({
    queryKey: ['nutrition-countries'],
    queryFn: async (): Promise<{ country: string; pet_count: number }[]> => {
      const { data, error } = await supabase.rpc('admin_get_nutrition_countries');
      
      if (error) throw error;
      return (data || []) as { country: string; pet_count: number }[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook: Get Nutrition Analytics
export const useNutritionAnalytics = (country?: string | null) => {
  return useQuery({
    queryKey: ['nutrition-analytics', country],
    queryFn: async (): Promise<NutritionAnalytics> => {
      const { data, error } = await supabase.rpc('admin_get_nutrition_analytics', {
        p_country: country || null,
      });
      
      if (error) throw error;
      return data as unknown as NutritionAnalytics;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook: Get Pets for Assignment with Filters
export const usePetsForAssignment = (filters: {
  brand?: string;
  city?: string;
  foodType?: string;
  hasProvider?: boolean | null;
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: ['pets-for-assignment', filters],
    queryFn: async (): Promise<PetForAssignment[]> => {
      const { data, error } = await supabase.rpc('admin_get_pets_for_assignment', {
        p_brand: filters.brand || null,
        p_city: filters.city || null,
        p_food_type: filters.foodType || null,
        p_has_provider: filters.hasProvider ?? null,
        p_limit: filters.limit || 50,
        p_offset: filters.offset || 0,
      });
      
      if (error) throw error;
      return (data || []) as PetForAssignment[];
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Hook: Get Active Providers
export const useActiveProviders = () => {
  return useQuery({
    queryKey: ['active-providers'],
    queryFn: async (): Promise<ActiveProvider[]> => {
      const { data, error } = await supabase.rpc('admin_get_active_providers');
      
      if (error) throw error;
      return (data || []) as ActiveProvider[];
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Hook: Bulk Assign Provider
export const useBulkAssignProvider = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ petIds, providerId }: { petIds: string[]; providerId: string }) => {
      const { data, error } = await supabase.rpc('admin_bulk_assign_provider', {
        p_pet_ids: petIds,
        p_provider_id: providerId,
        p_module_access: ['nutrition'],
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['pets-for-assignment'] });
      queryClient.invalidateQueries({ queryKey: ['nutrition-analytics'] });
      toast.success(`${data.assigned_count} mascotas asignadas exitosamente`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al asignar proveedor');
    },
  });
};

// Hook: Get Brand Aliases
export const useBrandAliases = (status: string = 'pending') => {
  return useQuery({
    queryKey: ['brand-aliases', status],
    queryFn: async (): Promise<BrandAlias[]> => {
      const { data, error } = await supabase.rpc('admin_get_brand_aliases', {
        p_status: status,
      });
      
      if (error) throw error;
      return (data || []) as BrandAlias[];
    },
    staleTime: 30 * 1000,
  });
};

// Hook: Merge Brand Alias
export const useMergeBrandAlias = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ aliasId, canonicalBrandId }: { aliasId: string; canonicalBrandId: number }) => {
      const { data, error } = await supabase.rpc('admin_merge_brand_alias', {
        p_alias_id: aliasId,
        p_canonical_brand_id: canonicalBrandId,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['brand-aliases'] });
      queryClient.invalidateQueries({ queryKey: ['nutrition-analytics'] });
      toast.success(`"${data.alias_text}" fusionado con "${data.canonical_brand}" (${data.updated_nutrition_records} registros actualizados)`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al fusionar marca');
    },
  });
};

// Hook: Get Geographic Distribution
export const useGeoDistribution = () => {
  return useQuery({
    queryKey: ['nutrition-geo-distribution'],
    queryFn: async (): Promise<GeoDistribution[]> => {
      const { data, error } = await supabase.rpc('admin_get_nutrition_geo_distribution');
      
      if (error) throw error;
      return (data || []) as GeoDistribution[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook: Get Food Brands for Autocomplete
export const useFoodBrands = () => {
  return useQuery({
    queryKey: ['food-brands'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pet_food_brands')
        .select('id, name, manufacturer, is_prescription')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Hook: Get Farm Feed Categories
export const useFarmFeedCategories = () => {
  return useQuery({
    queryKey: ['farm-feed-categories'],
    queryFn: async (): Promise<FarmFeedCategory[]> => {
      const { data, error } = await supabase
        .from('farm_feed_categories')
        .select('id, name, description, applies_to_species')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return (data || []) as FarmFeedCategory[];
    },
    staleTime: 30 * 60 * 1000,
  });
};

// Hook: Categorize Farm Feed Alias
export const useCategorizeFarmFeed = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ aliasId, feedCategoryId }: { aliasId: string; feedCategoryId: number }) => {
      const { data, error } = await supabase
        .from('brand_aliases')
        .update({
          canonical_feed_category_id: feedCategoryId,
          status: 'merged',
          merged_at: new Date().toISOString(),
        })
        .eq('id', aliasId)
        .select('alias_text')
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-aliases'] });
      queryClient.invalidateQueries({ queryKey: ['nutrition-analytics'] });
    },
    onError: (error: any) => {
      console.error('Error categorizing farm feed:', error);
    },
  });
};
